import { usePortalAccess } from '../../components/PortalAccess';
import { useEffect, useState } from 'react'
import { getPlatforms, updatePlatform, getCategories, createCategory, deleteCategory, syncVK, getVKOAuthUrl, exchangeVKCode, getMonthlyStats, createMonthlyStats, updateMonthlyStats, deleteMonthlyStats, getPlatform, syncTG, getIGStatus, syncIG } from '../../utils/smmApi'
import { formatMonth } from '../../utils/smmFormat'
import styles from './SmmSettings.module.css'

const pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

async function waitForInstagramSync(previousLastSyncAt, onStatus) {
  const deadline = Date.now() + 10 * 60 * 1000
  while (Date.now() < deadline) {
    await pause(2500)
    const status = await getIGStatus({ fresh: true })
    onStatus(status)
    const state = String(status.sync_status || status.state || status.last_sync_result?.status || '').toLowerCase()
    const running = status.running ?? status.sync_running ?? status.in_progress ?? ['running', 'started', 'pending'].includes(state)
    if (!running && (status.last_sync_at !== previousLastSyncAt || ['success', 'completed', 'done', 'error', 'failed'].includes(state))) {
      return status
    }
  }
  throw new Error('Синхронизация выполняется дольше 10 минут')
}

export default function SmmSettings() {
  const { permissions } = usePortalAccess();
  const canManage = permissions['smm.manage'] === true;
  const [platforms, setPlatforms] = useState([])
  const [categories, setCategories] = useState([])
  const [saving, setSaving] = useState(null)
  const [tokens, setTokens] = useState({})
  const [groupIds, setGroupIds] = useState({})
  const [savedGroupIds, setSavedGroupIds] = useState([])
  const [syncing, setSyncing] = useState(null)
  const [syncMsg, setSyncMsg] = useState({})
  const [vkCode, setVkCode] = useState('')
  const [vkOAuthMsg, setVkOAuthMsg] = useState('')
  const [vkExchanging, setVkExchanging] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [newCatColor, setNewCatColor] = useState('#6366f1')

  // VK manual stats (for community metrics: visitors, content_views, messages, new_messages)
  const [vkPlatformObj, setVkPlatformObj] = useState(null)
  const [vkStats, setVkStats] = useState([])
  const [vkShowForm, setVkShowForm] = useState(false)
  const [vkEditId, setVkEditId] = useState(null)
  const [vkSaving, setVkSaving] = useState(false)
  const [vkMsg, setVkMsg] = useState('')
  const [vkForm, setVkForm] = useState(() => {
    const now = new Date()
    const m = now.getMonth() === 0 ? 12 : now.getMonth()
    const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
    return { year: y, month: m, visitors: '', content_views: '', content_reach: '', messages: '', new_messages: '' }
  })

  // Telegram
  const [tgChannelId, setTgChannelId] = useState('')
  const [tgPlatform, setTgPlatform] = useState(null)
  const [tgStats, setTgStats] = useState([])
  const [tgForm, setTgForm] = useState(() => {
    const now = new Date()
    const m = now.getMonth() === 0 ? 12 : now.getMonth()
    const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
    return { year: y, month: m, subscribers: '', notifications_on_pct: '' }
  })
  const [tgSaving, setTgSaving] = useState(false)
  const [tgMsg, setTgMsg] = useState('')
  const [tgShowForm, setTgShowForm] = useState(false)
  const [tgEditId, setTgEditId] = useState(null)

  const [igStatus, setIgStatus] = useState(null)



  useEffect(() => {
    getPlatforms().then(setPlatforms)
    getCategories().then(setCategories)
    if (permissions['smm.instagram']) getIGStatus().then(setIgStatus).catch(console.error)
    const stored = localStorage.getItem('vk_saved_group_ids')
    if (stored) setSavedGroupIds(JSON.parse(stored))
    // Load Telegram data
    if (permissions['smm.telegram']) getPlatform('telegram').then(p => {
      setTgPlatform(p)
      return getMonthlyStats('telegram')
    }).then(setTgStats).catch(console.error)
    // Load VK stats for manual community metrics
    if (permissions['smm.vk']) getPlatform('vk').then(p => {
      setVkPlatformObj(p)
      return getMonthlyStats('vk')
    }).then(setVkStats).catch(console.error)
  }, [])

  const handleToggle = async (slug, current) => {
    setSaving(slug)
    try {
      const updated = await updatePlatform(slug, { is_active: !current })
      setPlatforms(prev => prev.map(p => p.slug === slug ? updated : p))
    } finally {
      setSaving(null)
    }
  }

  const handleSaveToken = async (slug) => {
    setSaving(slug + '_token')
    try {
      const updated = await updatePlatform(slug, { api_token: tokens[slug] || '' })
      setPlatforms(prev => prev.map(p => p.slug === slug ? updated : p))
      setTokens(t => { const n = { ...t }; delete n[slug]; return n })
      alert('Токен сохранён')
    } finally {
      setSaving(null)
    }
  }

  const handleVKOAuth = async () => {
    try {
      const { url } = await getVKOAuthUrl()
      window.open(url, '_blank')
    } catch (e) {
      setVkOAuthMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message))
    }
  }

  const handleVKExchange = async () => {
    if (!vkCode.trim()) return
    setVkExchanging(true)
    setVkOAuthMsg('')
    try {
      await exchangeVKCode(vkCode.trim())
      setVkOAuthMsg('Токен успешно сохранён!')
      setVkCode('')
    } catch (e) {
      setVkOAuthMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message))
    } finally {
      setVkExchanging(false)
    }
  }

  const handleSync = async (slug) => {
    setSyncing(slug)
    setSyncMsg(m => ({ ...m, [slug]: '' }))
    try {
      if (slug === 'vk') {
        const gid = groupIds['vk'] || ''
        await syncVK(gid)
        setSyncMsg(m => ({ ...m, vk: 'Синхронизация запущена в фоне...' }))
        if (gid) {
          setSavedGroupIds(prev => {
            const updated = [gid, ...prev.filter(x => x !== gid)].slice(0, 10)
            localStorage.setItem('vk_saved_group_ids', JSON.stringify(updated))
            return updated
          })
        }
      } else if (slug === 'telegram') {
        const r = await syncTG()
        setSyncMsg(m => ({ ...m, telegram: r.ok ? `Готово: ${r.synced_posts} постов` : `Ошибка: ${r.error}` }))
      } else if (slug === 'instagram') {
        const previousLastSyncAt = igStatus?.last_sync_at || null
        const r = await syncIG()
        const state = String(r.status || '').toLowerCase()
        const finalStatus = ['started', 'running', 'pending', 'already_running'].includes(state)
          ? await waitForInstagramSync(previousLastSyncAt, setIgStatus)
          : await getIGStatus({ fresh: true })
        const result = finalStatus.last_sync_result || r
        if (result.ok === false || ['error', 'failed'].includes(String(result.status || '').toLowerCase())) {
          throw new Error(result.error || result.message || 'Ошибка Meta API')
        }
        const count = result.synced_posts ?? result.posts_synced ?? result.media_synced
        setSyncMsg(m => ({ ...m, instagram: count !== null && count !== undefined ? `Готово: ${count} публикаций` : 'Синхронизация завершена' }))
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Ошибка'
      setSyncMsg(m => ({ ...m, [slug]: `Ошибка: ${msg}` }))
    } finally {
      setSyncing(null)
    }
  }

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return
    const cat = await createCategory({ name: newCatName.trim(), color: newCatColor })
    setCategories(prev => [...prev, cat])
    setNewCatName('')
    setNewCatColor('#6366f1')
  }

  const handleDeleteCategory = async (id) => {
    if (!confirm('Удалить рубрику?')) return
    await deleteCategory(id)
    setCategories(prev => prev.filter(c => c.id !== id))
  }

  const API_PLATFORMS = platforms.filter(p => ['vk', 'telegram', 'instagram'].includes(p.slug))
  const MANUAL_PLATFORMS = platforms.filter(p => ['max'].includes(p.slug))
  const igResult = igStatus?.last_sync_result || igStatus?.result || {}
  const igFailed = ['error', 'failed'].includes(String(igStatus?.sync_status || igResult.status || '').toLowerCase())
  const igError = igResult.error || igResult.message || ''
  const FUTURE_PLATFORMS = platforms.filter(p => ['youtube', 'tiktok', 'rutube', 'dzen'].includes(p.slug))

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Настройки</h1>

      {/* API platforms */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>API-платформы</h2>
        <div className={styles.platformList}>
          {API_PLATFORMS.map(p => (
            <div key={p.slug} className={styles.platformItem}>
              <div className={styles.platformInfo}>
                <span className={styles.platformDot} style={{ background: p.color }} />
                <span className={styles.platformName}>{p.name}</span>
                <span className={styles.platformSource}>{p.slug === 'instagram' ? 'Meta API' : p.data_source}</span>
              </div>
              <div className={styles.platformActions}>
                <Toggle
                  value={p.is_active}
                  loading={saving === p.slug}
                  onChange={() => handleToggle(p.slug, p.is_active)}
                />
              </div>
              {p.is_active && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  {p.slug === 'vk' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                        Шаг 1: открой авторизацию VK → разреши → скопируй <code>code=...</code> из URL
                      </p>
                      <div className={styles.tokenRow}>
                        <button className="btn btn-secondary btn-sm" onClick={handleVKOAuth}>
                          Открыть авторизацию VK
                        </button>
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>Шаг 2: скопируй <code>access_token=...</code> из URL и вставь сюда</p>
                      <div className={styles.tokenRow}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="access_token из URL"
                          value={vkCode}
                          onChange={e => setVkCode(e.target.value)}
                          style={{ maxWidth: 320 }}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleVKExchange}
                          disabled={vkExchanging || !vkCode.trim()}
                        >
                          {vkExchanging ? 'Сохранение...' : 'Сохранить токен'}
                        </button>
                      </div>
                      {vkOAuthMsg && (
                        <p style={{ fontSize: 13, color: vkOAuthMsg.startsWith('Ошибка') ? 'var(--er-low)' : 'var(--er-high)', margin: 0 }}>
                          {vkOAuthMsg}
                        </p>
                      )}
                    </div>
                  ) : p.slug === 'instagram' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                        Доступ настроен на сервере через защищённые переменные окружения. Токен в интерфейсе не отображается.
                      </p>
                      <p style={{ fontSize: 12, color: igStatus?.configured === false || igFailed ? 'var(--danger)' : 'var(--success)', margin: 0 }}>
                        {igStatus?.configured === false
                          ? 'Meta API не настроен'
                          : igFailed
                            ? `Ошибка последней синхронизации${igError ? `: ${igError}` : ''}`
                            : `Meta API подключён · последняя синхронизация: ${igStatus?.last_sync_at ? new Date(igStatus.last_sync_at).toLocaleString('ru-RU') : 'ещё не запускалась'}`}
                      </p>
                    </div>
                  ) : (
                    <div className={styles.tokenRow}>
                      <input
                        type="password"
                        className="form-input"
                        placeholder={`${p.name} API токен`}
                        value={tokens[p.slug] ?? ''}
                        onChange={e => setTokens(t => ({ ...t, [p.slug]: e.target.value }))}
                        style={{ maxWidth: 400 }}
                      />
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleSaveToken(p.slug)}
                        disabled={saving === p.slug + '_token' || !tokens[p.slug]}
                      >
                        Сохранить токен
                      </button>
                    </div>
                  )}
                  {p.slug === 'vk' && (
                    <div className={styles.tokenRow}>
                      <input
                        type="text"
                        list="vk-group-ids"
                        className="form-input"
                        placeholder="ID группы ВКонтакте (число, например 123456)"
                        value={groupIds[p.slug] ?? ''}
                        onChange={e => setGroupIds(g => ({ ...g, [p.slug]: e.target.value }))}
                        style={{ maxWidth: 400 }}
                      />
                      {savedGroupIds.length > 0 && (
                        <datalist id="vk-group-ids">
                          {savedGroupIds.map(id => <option key={id} value={id} />)}
                        </datalist>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleSync(p.slug)}
                        disabled={!canManage || !permissions[`smm.${p.slug}`] || syncing === p.slug || !groupIds[p.slug]}
                      >
                        {syncing === p.slug ? 'Запуск...' : 'Синхронизировать'}
                      </button>
                    </div>
                  )}
                  {p.slug === 'telegram' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                        Токен TGStat и ID канала задаются в <code>.env</code> (TGSTAT_TOKEN, TGSTAT_CHANNEL_ID)
                      </p>
                      <div className={styles.tokenRow}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="ID канала (например @mychannel) — опционально"
                          value={tgChannelId}
                          onChange={e => setTgChannelId(e.target.value)}
                          style={{ maxWidth: 320 }}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSync(p.slug)}
                          disabled={!canManage || !permissions[`smm.${p.slug}`] || syncing === p.slug}
                        >
                          {syncing === p.slug ? 'Запуск...' : 'Синхронизировать'}
                        </button>
                      </div>
                    </div>
                  )}
                  {p.slug === 'instagram' && (
                    <div className={styles.tokenRow}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleSync(p.slug)}
                        disabled={!canManage || !permissions[`smm.${p.slug}`] || syncing === p.slug || igStatus?.configured === false}
                      >
                        {syncing === p.slug ? 'Синхронизация…' : 'Синхронизировать сейчас'}
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Автоматический запуск — ежедневно в 03:00 МСК
                      </span>
                    </div>
                  )}
                  {syncMsg[p.slug] && (
                    <p style={{ fontSize: 13, color: syncMsg[p.slug].startsWith('Ошибка') ? 'var(--er-low)' : 'var(--er-high)', margin: 0 }}>
                      {syncMsg[p.slug]}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* VK community metrics manual entry */}
      {canManage && vkPlatformObj?.is_active && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#4C75A3', marginRight: 4 }} />
            ВКонтакте — ручной ввод метрик
          </h2>
          <p className={styles.hint}>Посещения профиля, просмотры контента, сообщения — если данные не заполнились после синхронизации.</p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => {
              setVkEditId(null)
              const now = new Date()
              const m = now.getMonth() === 0 ? 12 : now.getMonth()
              const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
              setVkForm({ year: y, month: m, content_reach: '', visitors: '', content_views: '', messages: '', new_messages: '' })
              setVkShowForm(true)
              setTimeout(() => document.getElementById('vk-manual-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
            }}>
              + Внести данные за месяц
            </button>
          </div>

          {vkStats.filter(s => s.visitors !== null || s.content_views !== null || s.messages !== null).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[...vkStats].reverse().slice(0, 6).map(s =>
                (s.visitors !== null || s.content_views !== null || s.messages !== null) ? (
                  <div key={s.id} className={styles.platformItem} style={{ padding: '8px 16px' }}>
                    <div className={styles.platformInfo}>
                      <span className={styles.platformName}>{formatMonth(s.year, s.month)}</span>
                      {s.visitors !== null && <span className={styles.platformSource}>Посещ.: {s.visitors.toLocaleString('ru-RU')}</span>}
                      {s.content_views !== null && <span className={styles.platformSource}>Просм.: {s.content_views.toLocaleString('ru-RU')}</span>}
                      {s.messages !== null && <span className={styles.platformSource}>Сообщ.: {s.messages.toLocaleString('ru-RU')}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setVkEditId(s.id)
                        setVkForm({
                          year: s.year, month: s.month,
                          visitors: s.visitors?.toString() ?? '',
                          content_views: s.content_views?.toString() ?? '',
                          content_reach: s.content_reach?.toString() ?? '',
                          messages: s.messages?.toString() ?? '',
                          new_messages: s.new_messages?.toString() ?? '',
                        })
                        setVkShowForm(true)
                        setTimeout(() => document.getElementById('vk-manual-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
                      }}>
                        Изменить
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={async () => {
                        if (!confirm(`Удалить данные за ${formatMonth(s.year, s.month)}?`)) return
                        try {
                          await deleteMonthlyStats(s.id)
                          setVkStats(prev => prev.filter(x => x.id !== s.id))
                        } catch (e) {
                          alert('Ошибка: ' + (e?.response?.data?.detail || e?.message))
                        }
                      }}>✕</button>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          )}

          {vkShowForm && (
            <div id="vk-manual-form" style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: 20, maxWidth: 680,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600 }}>
                  {vkEditId ? 'Редактировать данные' : 'Внести данные за месяц'}
                </h3>
                <button className="btn btn-ghost btn-sm" onClick={() => { setVkShowForm(false); setVkEditId(null) }}>Закрыть</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 150px 1fr 1fr', gap: 12, alignItems: 'flex-end', marginBottom: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Год</label>
                  <select className="form-input" value={vkForm.year}
                    onChange={e => setVkForm(f => ({ ...f, year: parseInt(e.target.value) }))}>
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Месяц</label>
                  <select className="form-input" value={vkForm.month}
                    onChange={e => setVkForm(f => ({ ...f, month: parseInt(e.target.value) }))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{formatMonth(vkForm.year, m)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Охват пользователей</label>
                  <input type="number" className="form-input" min={0} step={1} placeholder="reach.reach"
                    value={vkForm.content_reach} onChange={e => setVkForm(f => ({ ...f, content_reach: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Посещения профиля</label>
                  <input type="number" className="form-input" min={0} step={1} placeholder="reach.visitors"
                    value={vkForm.visitors} onChange={e => setVkForm(f => ({ ...f, visitors: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Просмотры контента</label>
                  <input type="number" className="form-input" min={0} step={1} placeholder="reach.views"
                    value={vkForm.content_views} onChange={e => setVkForm(f => ({ ...f, content_views: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Сообщения группе</label>
                  <input type="number" className="form-input" min={0} step={1} placeholder="activity.messages"
                    value={vkForm.messages} onChange={e => setVkForm(f => ({ ...f, messages: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Написали впервые</label>
                  <input type="number" className="form-input" min={0} step={1} placeholder="activity.new_messages"
                    value={vkForm.new_messages} onChange={e => setVkForm(f => ({ ...f, new_messages: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setVkShowForm(false); setVkEditId(null) }}>Отмена</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={vkSaving || (!vkForm.content_reach && !vkForm.visitors && !vkForm.content_views && !vkForm.messages && !vkForm.new_messages)}
                  onClick={async () => {
                    setVkSaving(true)
                    setVkMsg('')
                    try {
                      const payload = {}
                      if (vkForm.content_reach) payload.content_reach = parseInt(vkForm.content_reach)
                      if (vkForm.visitors) payload.visitors = parseInt(vkForm.visitors)
                      if (vkForm.content_views) payload.content_views = parseInt(vkForm.content_views)
                      if (vkForm.messages) payload.messages = parseInt(vkForm.messages)
                      if (vkForm.new_messages) payload.new_messages = parseInt(vkForm.new_messages)
                      let saved
                      if (vkEditId) {
                        saved = await updateMonthlyStats(vkEditId, payload)
                        setVkStats(prev => prev.map(s => s.id === vkEditId ? saved : s))
                      } else {
                        const existing = vkStats.find(s => s.year === vkForm.year && s.month === vkForm.month)
                        if (existing) {
                          saved = await updateMonthlyStats(existing.id, payload)
                          setVkStats(prev => prev.map(s => s.id === existing.id ? saved : s))
                        } else {
                          saved = await createMonthlyStats({
                            platform_id: vkPlatformObj.id,
                            year: vkForm.year,
                            month: vkForm.month,
                            ...payload,
                          })
                          setVkStats(prev => [...prev, saved].sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month)))
                        }
                      }
                      setVkMsg('Сохранено')
                      setVkShowForm(false)
                      setVkEditId(null)
                    } catch (e) {
                      setVkMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message))
                    } finally {
                      setVkSaving(false)
                    }
                  }}
                >
                  {vkSaving ? <span className="spinner" /> : vkEditId ? 'Сохранить изменения' : 'Сохранить'}
                </button>
                {vkMsg && (
                  <span style={{ fontSize: 13, color: vkMsg.startsWith('Ошибка') ? 'var(--danger)' : 'var(--success)' }}>
                    {vkMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Manual platforms */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Ручные платформы</h2>
        <div className={styles.platformList}>
          {MANUAL_PLATFORMS.map(p => (
            <div key={p.slug} className={styles.platformItem}>
              <div className={styles.platformInfo}>
                <span className={styles.platformDot} style={{ background: p.color }} />
                <span className={styles.platformName}>{p.name}</span>
                <span className={styles.platformSource}>Ручной ввод</span>
              </div>
              <Toggle
                value={p.is_active}
                loading={saving === p.slug}
                onChange={() => handleToggle(p.slug, p.is_active)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Telegram manual data */}
      {canManage && tgPlatform?.is_active && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#2AABEE', marginRight: 4 }} />
            Telegram — ручной ввод
          </h2>
          <p className={styles.hint}>Вводите подписчиков и % включённых уведомлений ежемесячно из статистики Telegram.</p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" onClick={() => {
              setTgEditId(null)
              const now = new Date()
              const m = now.getMonth() === 0 ? 12 : now.getMonth()
              const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
              setTgForm({ year: y, month: m, subscribers: '', notifications_on_pct: '' })
              setTgShowForm(true)
              setTimeout(() => document.getElementById('tg-settings-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
            }}>
              + Внести данные за месяц
            </button>
          </div>

          {tgStats.filter(s => s.subscribers !== null || s.notifications_on_pct !== null).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[...tgStats].reverse().slice(0, 6).map(s => (s.subscribers !== null || s.notifications_on_pct !== null) ? (
                <div key={s.id} className={styles.platformItem} style={{ padding: '8px 16px' }}>
                  <div className={styles.platformInfo}>
                    <span className={styles.platformName}>{formatMonth(s.year, s.month)}</span>
                    {s.subscribers !== null && (
                      <span className={styles.platformSource}>{s.subscribers.toLocaleString('ru-RU')} подп.</span>
                    )}
                    {s.notifications_on_pct !== null && (
                      <span className={styles.platformSource}>Вкл. уведомления: {s.notifications_on_pct}%</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      setTgEditId(s.id)
                      setTgForm({
                        year: s.year, month: s.month,
                        subscribers: s.subscribers?.toString() ?? '',
                        notifications_on_pct: s.notifications_on_pct?.toString() ?? '',
                      })
                      setTgShowForm(true)
                      setTimeout(() => document.getElementById('tg-settings-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
                    }}>
                      Изменить
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={async () => {
                      if (!confirm(`Удалить данные за ${formatMonth(s.year, s.month)}?`)) return
                      try {
                        await deleteMonthlyStats(s.id)
                        setTgStats(prev => prev.filter(x => x.id !== s.id))
                      } catch (e) {
                        alert('Ошибка: ' + (e?.response?.data?.detail || e?.message))
                      }
                    }}>
                      ✕
                    </button>
                  </div>
                </div>
              ) : null)}
            </div>
          )}

          {tgShowForm && (
            <div id="tg-settings-form" style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: 20, maxWidth: 600,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600 }}>
                  {tgEditId ? 'Редактировать данные' : 'Внести данные за месяц'}
                </h3>
                <button className="btn btn-ghost btn-sm" onClick={() => { setTgShowForm(false); setTgEditId(null) }}>
                  Закрыть
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 150px 1fr 1fr', gap: 12, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Год</label>
                  <select className="form-input" value={tgForm.year}
                    onChange={e => setTgForm(f => ({ ...f, year: parseInt(e.target.value) }))}>
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Месяц</label>
                  <select className="form-input" value={tgForm.month}
                    onChange={e => setTgForm(f => ({ ...f, month: parseInt(e.target.value) }))}>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{formatMonth(tgForm.year, m)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Подписчики</label>
                  <input type="number" className="form-input" min={0} step={1}
                    placeholder="Например 598"
                    value={tgForm.subscribers}
                    onChange={e => setTgForm(f => ({ ...f, subscribers: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Вкл. уведомления, %</label>
                  <input type="number" className="form-input" min={0} max={100} step={0.1}
                    placeholder="Например 72.5"
                    value={tgForm.notifications_on_pct}
                    onChange={e => setTgForm(f => ({ ...f, notifications_on_pct: e.target.value }))} />
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setTgShowForm(false); setTgEditId(null) }}
                >
                  Отмена
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={tgSaving || (!tgForm.subscribers && !tgForm.notifications_on_pct)}
                  onClick={async () => {
                    setTgSaving(true)
                    setTgMsg('')
                    try {
                      const payload = {}
                      if (tgForm.subscribers) payload.subscribers = parseInt(tgForm.subscribers)
                      if (tgForm.notifications_on_pct) payload.notifications_on_pct = parseFloat(tgForm.notifications_on_pct)
                      let saved
                      if (tgEditId) {
                        saved = await updateMonthlyStats(tgEditId, payload)
                        setTgStats(prev => prev.map(s => s.id === tgEditId ? saved : s))
                      } else {
                        const existing = tgStats.find(s => s.year === tgForm.year && s.month === tgForm.month)
                        if (existing) {
                          saved = await updateMonthlyStats(existing.id, payload)
                          setTgStats(prev => prev.map(s => s.id === existing.id ? saved : s))
                        } else {
                          saved = await createMonthlyStats({
                            platform_id: tgPlatform.id,
                            year: tgForm.year,
                            month: tgForm.month,
                            ...payload,
                          })
                          setTgStats(prev => [...prev, saved].sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month)))
                        }
                      }
                      setTgMsg('Сохранено')
                      setTgShowForm(false)
                      setTgEditId(null)
                    } catch (e) {
                      setTgMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message))
                    } finally {
                      setTgSaving(false)
                    }
                  }}
                >
                  {tgSaving ? <span className="spinner" /> : tgEditId ? 'Сохранить изменения' : 'Сохранить'}
                </button>
                {tgMsg && (
                  <span style={{ fontSize: 13, color: tgMsg.startsWith('Ошибка') ? 'var(--danger)' : 'var(--success)' }}>
                    {tgMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Future platforms */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Будущие платформы</h2>
        <p className={styles.hint}>Зарезервированы в БД и UI. Активируются при начале ведения.</p>
        <div className={styles.platformList}>
          {FUTURE_PLATFORMS.map(p => (
            <div key={p.slug} className={`${styles.platformItem} ${styles.futureItem}`}>
              <div className={styles.platformInfo}>
                <span className={styles.platformDot} style={{ background: p.color }} />
                <span className={styles.platformName}>{p.name}</span>
                <span className={styles.comingSoon}>скоро</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Рубрики контента</h2>
        <div className={styles.catList}>
          {categories.map(c => (
            <div key={c.id} className={styles.catItem}>
              <span className={styles.catDot} style={{ background: c.color }} />
              <span className={styles.catName}>{c.name}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteCategory(c.id)}>✕</button>
            </div>
          ))}
        </div>
        <div className={styles.addCat}>
          <input
            type="text"
            className="form-input"
            placeholder="Новая рубрика..."
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
            style={{ maxWidth: 240 }}
          />
          <input
            type="color"
            value={newCatColor}
            onChange={e => setNewCatColor(e.target.value)}
            style={{ width: 40, height: 36, border: 'none', background: 'none', cursor: 'pointer' }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleAddCategory} disabled={!newCatName.trim()}>
            + Добавить
          </button>
        </div>
      </section>
    </div>
  )
}

function Toggle({ value, loading, onChange }) {
  return (
    <button
      className={`${styles.toggle} ${value ? styles.toggleOn : styles.toggleOff}`}
      onClick={onChange}
      disabled={loading}
    >
      {loading ? <span className="spinner" style={{ width: 12, height: 12 }} /> : (
        <span className={styles.toggleThumb} />
      )}
    </button>
  )
}
